/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { logger } from "@/lib/utils/logger";
import React, { createContext, useContext, useEffect, useState } from "react";

interface AccessibilityContextType {
  highContrast: boolean;
  toggleHighContrast: () => void;
  fontSize: "normal" | "large" | "xlarge";
  setFontSize: (size: "normal" | "large" | "xlarge") => void;
}

const AccessibilityContext = createContext<
  AccessibilityContextType | undefined
>(undefined);

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error(
      "useAccessibility must be used within an AccessibilityProvider",
    );
  }
  return context;
}

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export function AccessibilityProvider({
  children,
}: AccessibilityProviderProps) {
  const [highContrast, setHighContrast] = useState(false);
  const [fontSize, setFontSize] = useState<"normal" | "large" | "xlarge">(
    "normal",
  );

  // Load accessibility preferences from localStorage
  useEffect(() => {
    try {
      const savedHighContrast = localStorage.getItem("highContrast") === "true";
      const savedFontSize =
        (localStorage.getItem("fontSize") as "normal" | "large" | "xlarge") ||
        "normal";

      setHighContrast(savedHighContrast);
      setFontSize(savedFontSize);
    } catch (error) {
      // Handle localStorage access errors (e.g., in incognito mode)
      logger.error("Failed to load accessibility preferences:", error);
    }
  }, []);

  // Apply accessibility settings to document
  useEffect(() => {
    const root = document.documentElement;

    // High contrast mode
    if (highContrast) {
      root.classList.add("high-contrast");
    } else {
      root.classList.remove("high-contrast");
    }

    // Font size
    root.classList.remove(
      "font-size-normal",
      "font-size-large",
      "font-size-xlarge",
    );
    root.classList.add(`font-size-${fontSize}`);

    // Save preferences
    try {
      localStorage.setItem("highContrast", highContrast.toString());
      localStorage.setItem("fontSize", fontSize);
    } catch (error) {
      // Handle localStorage access errors (e.g., in incognito mode)
      logger.error("Failed to save accessibility preferences:", error);
    }
  }, [highContrast, fontSize]);

  const toggleHighContrast = () => setHighContrast(!highContrast);

  const value: AccessibilityContextType = {
    highContrast,
    toggleHighContrast,
    fontSize,
    setFontSize,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

// Simple accessibility toolbar component
export function AccessibilityToolbar() {
  const { highContrast, toggleHighContrast, fontSize, setFontSize } =
    useAccessibility();

  const [isVisible, setIsVisible] = useState(false);

  return (
    <>
      {/* Hidden accessibility controls - only visible when needed */}
      {isVisible && (
        <div className="fixed bottom-4 right-4 z-50 space-y-3 rounded-lg border border-border bg-card p-4 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">
              Accessibility
            </h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Close accessibility menu"
            >
              ×
            </button>
          </div>

          {/* High Contrast Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="high-contrast"
              checked={highContrast}
              onChange={toggleHighContrast}
              className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/50"
            />
            <label htmlFor="high-contrast" className="text-sm text-foreground">
              High Contrast
            </label>
          </div>

          {/* Font Size Control */}
          <div className="space-y-1">
            <label className="text-sm text-foreground">Font Size</label>
            <div className="flex space-x-1">
              {(["normal", "large", "xlarge"] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`px-2 py-1 text-xs rounded ${
                    fontSize === size
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {size === "normal" ? "A" : size === "large" ? "A+" : "A++"}
                </button>
              ))}
            </div>
          </div>

          {/* Skip to main content link */}
          <a
            href="#main-content"
            className="block text-sm text-primary underline hover:text-primary/80"
          >
            Skip to main content
          </a>
        </div>
      )}

      {/* Small accessibility button - only shows when needed */}
      <div className="fixed bottom-4 right-4 z-40">
        <button
          onClick={() => setIsVisible(!isVisible)}
          className="rounded-full bg-primary p-2 text-primary-foreground shadow-lg transition-colors duration-200 hover:bg-primary/80"
          aria-label="Open accessibility menu"
          title="Accessibility options"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </button>
      </div>
    </>
  );
}
